import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAuthStore } from '../store/authStore';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import {
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import { Box, Typography } from '@mui/material';
import { log } from '../services/logging';
import { IS_PRODUCTION } from '../services/environment';
import PublicPageLayout from '../components/layout/PublicPageLayout';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const {
    login,
    resendMagicLink,
    clearEmailSentState,
    isAuthenticated,
    isLoading,
    user,
    emailSent,
    sentEmail,
    lastEmailSentAt,
    error,
  } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/leads');
    }
  }, [isAuthenticated, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      return;
    }

    try {
      await login(email);
      localStorage.setItem('userEmail', email);
    } catch (error) {
      console.error('Login error:', error);
    }
  };

  const handleResendEmail = async () => {
    try {
      await resendMagicLink();
    } catch (error) {
      console.error('Resend error:', error);
    }
  };

  const handleTryDifferentEmail = () => {
    clearEmailSentState();
    setEmail('');
  };

  // Calculate if we can show resend button (60 seconds after last email)
  const canResend = lastEmailSentAt
    ? Date.now() - lastEmailSentAt > 60000
    : false;
  const timeUntilResend = lastEmailSentAt
    ? Math.max(0, 60 - Math.floor((Date.now() - lastEmailSentAt) / 1000))
    : 0;

  // Timer for resend countdown
  const [countdown, setCountdown] = useState(timeUntilResend);

  useEffect(() => {
    if (!emailSent || canResend) {
      setCountdown(0);
      return;
    }

    const timer = setInterval(() => {
      const newTimeUntilResend = lastEmailSentAt
        ? Math.max(0, 60 - Math.floor((Date.now() - lastEmailSentAt) / 1000))
        : 0;
      setCountdown(newTimeUntilResend);

      if (newTimeUntilResend === 0) {
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [emailSent, canResend, lastEmailSentAt]);

  // Test error logging function
  const handleTestErrorLogging = () => {
    const testError = new Error('Test login page error from user interface');

    const contextInfo = {
      page: 'login',
      currentEmail: email,
      isAuthenticated,
      isLoading,
      screenWidth: window.innerWidth,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
      currentUrl: window.location.href,
    };

    const userInfo = user
      ? {
          userId: user.id,
          userEmail: user.email,
          userName: user.firstName
            ? `${user.firstName} ${user.lastName || ''}`.trim()
            : undefined,
          onboardingCompleted: user.onboardingCompleted,
          accountCreated: user.createdAt,
        }
      : {
          userStatus: 'not_authenticated',
        };

    console.log('✅ Test error logged successfully! Check console and Sentry.');

    // Test both console and Sentry logging
    log(
      'Test error logging from Login Page component',
      {
        ...contextInfo,
        ...userInfo,
        testScenario: 'ui_test_button',
        severity: 'test',
      },
      testError
    );
  };

  return (
    <PublicPageLayout>
      {!emailSent ? (
        <>
          <Typography
            variant="h4"
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              textAlign: 'center',
              color: 'text.primary',
              mb: 4,
            }}
          >
            Log in to Fieldvest Admin
          </Typography>

          <Box
            component="form"
            onSubmit={handleLogin}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 1, md: 3 },
            }}
          >
            <Input
              label="Email Address"
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              leftIcon={
                <EnvelopeIcon style={{ width: '18px', height: '18px' }} />
              }
              aria-required="true"
            />

            {error && (
              <Typography
                sx={{
                  color: 'error.main',
                  fontSize: '0.875rem',
                  textAlign: 'center',
                }}
              >
                {error}
              </Typography>
            )}

            <Button
              type="submit"
              isLoading={isLoading}
              isFullWidth
              disabled={!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)}
              rightIcon={
                <ChevronRightIcon style={{ width: '18px', height: '18px' }} />
              }
            >
              Continue
            </Button>
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              mb: 4,
            }}
          >
            <CheckCircleIcon
              style={{
                width: '48px',
                height: '48px',
                color: 'var(--mui-palette-success-main)',
                marginBottom: '16px',
              }}
            />
            <Typography
              variant="h4"
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
              }}
            >
              Check your email!
            </Typography>
            <Typography
              sx={{
                fontSize: '1rem',
                color: 'text.secondary',
                mb: 1,
              }}
            >
              We've sent a magic link to
            </Typography>
            <Typography
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                color: 'text.primary',
                mb: 3,
              }}
            >
              {sentEmail}
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: 'text.secondary',
                lineHeight: 1.6,
              }}
            >
              Click the link in your email to log in. The link will expire in 1
              hour.
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {countdown > 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  p: 2,
                  backgroundColor: 'grey.50',
                  borderRadius: 1,
                  border: theme => `1px solid ${theme.palette.grey[200]}`,
                }}
              >
                <ClockIcon style={{ width: '16px', height: '16px' }} />
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                  }}
                >
                  Resend available in {countdown} seconds
                </Typography>
              </Box>
            ) : (
              <Button
                onClick={handleResendEmail}
                isLoading={isLoading}
                isFullWidth
                variant="outlined"
              >
                Resend magic link
              </Button>
            )}

            <Button
              onClick={handleTryDifferentEmail}
              isFullWidth
              variant="text"
            >
              Try a different email
            </Button>
          </Box>
        </>
      )}

      {/* Test Error Logging Button - Development Only */}
      {IS_PRODUCTION ? null : (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Box
            sx={{
              backgroundColor: 'grey.100',
              border: theme => `1px dashed ${theme.palette.grey[400]}`,
              borderRadius: '0.5rem',
              p: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.75rem',
                color: 'text.secondary',
                mb: 1,
                fontWeight: 600,
              }}
            >
              🧪 Development Testing
            </Typography>
            <Button
              onClick={handleTestErrorLogging}
              sx={{
                fontSize: '0.75rem',
                padding: '0.5rem 1rem',
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              Test Error Logging
            </Button>
            <Typography
              sx={{
                fontSize: '0.6875rem',
                color: 'text.secondary',
                mt: 1,
              }}
            >
              Logs a test error with user context to console and Sentry
            </Typography>
          </Box>
        </Box>
      )}
    </PublicPageLayout>
  );
}
